import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  AudioLines,
  CircleCheck,
  TriangleAlert,
  CircleX,
  type LucideIcon,
} from 'lucide-react';

export type AudioStatus =
  | 'initializing'
  | 'connected'
  | 'only-microphone'
  | 'only-tab'
  | 'no-audio';

interface AudioStatusBadgeProps {
  status: AudioStatus;
  shrinkDelay?: number;
}

const statusConfig: Record<
  AudioStatus,
  {
    icon: LucideIcon;
    i18nKey: string;
    fallback: string;
    className: string;
  }
> = {
  initializing: {
    icon: AudioLines,
    i18nKey: 'recorderControls.audioStatus.initializing',
    fallback: 'Initializing audio detection...',
    className: 'bg-muted text-muted-foreground',
  },
  connected: {
    icon: CircleCheck,
    i18nKey: 'recorderControls.audioStatus.connected',
    fallback: 'Audio connected',
    className: 'bg-green-500/15 text-green-700 dark:text-green-400',
  },
  'only-microphone': {
    icon: TriangleAlert,
    i18nKey: 'recorderControls.audioStatus.onlyMicrophone',
    fallback: 'Only microphone audio detected',
    className:
      'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
  },
  'only-tab': {
    icon: TriangleAlert,
    i18nKey: 'recorderControls.audioStatus.onlyTab',
    fallback: 'Only system audio detected',
    className:
      'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
  },
  'no-audio': {
    icon: CircleX,
    i18nKey: 'recorderControls.audioStatus.noAudio',
    fallback: 'Unable to detect audio',
    className: 'bg-red-500/15 text-red-700 dark:text-red-400',
  },
};

const AudioStatusBadge: React.FC<AudioStatusBadgeProps> = ({
  status,
  shrinkDelay = 5000,
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setIsCollapsed(false);

    // Don't auto-collapse error states — keep them visible
    if (status === 'no-audio') return undefined;

    const timer = setTimeout(() => {
      setIsCollapsed(true);
    }, shrinkDelay);

    return () => clearTimeout(timer);
  }, [status, shrinkDelay]);

  const config = statusConfig[status];
  const { icon: Icon, i18nKey, fallback, className } = config;
  const label = t(i18nKey, fallback);
  const showExpanded = !isCollapsed || isHovered;

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-full transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap flex-shrink-0 cursor-default',
        showExpanded ? 'gap-1.5 px-2.5 py-1' : 'gap-0 p-1',
        className,
      )}
      title={label}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Icon className="h-4.5 w-4.5 flex-shrink-0" />
      <span
        className={cn(
          'text-xs font-medium transition-all duration-300 ease-in-out',
          showExpanded ? 'max-w-[300px] opacity-100' : 'max-w-0 opacity-0 ml-0',
        )}
      >
        {label}
      </span>
    </div>
  );
};

export default AudioStatusBadge;
